import { getNumberFromQueryString } from '@if/core/utils'
import { Paper } from '@mui/material'
import { DataGrid, GridPaginationModel } from '@mui/x-data-grid'
import { useInteracWireByFilters } from '@src/api/interac-wire-api'
import { ESystemMatchingResult, InteracWire, InteracWireFiltersType } from '@src/types/interac'
import { useCallback, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import InteracWireDeleteDialog from './InteracWireDeleteDialog'
import InteracWireMatchDialog from './InteracWireMatchDialog'
import InteracWireMatchUnknownDialog from './InteracWireMatchUnknownDialog'
import { useUnmatchedInteracWireColumns } from './UnmatchedInteracWire-hook'
import UnmatchedInteracWireFilters from './UnmatchedInteracWireFilters'

const UnmatchedInteracWireList = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const [selectedDetailInteracWire, setSelectedDetailInteracWire] = useState<InteracWire | null>(null)
  const [selectedDeleteInteracWire, setSelectedDeleteInteracWire] = useState<InteracWire | null>(null)
  const [selectedUnknownInteracWire, setSelectedUnknownInteracWire] = useState<InteracWire | null>(null)
  const filterValues = useMemo<InteracWireFiltersType>(() => {
    return {
      limit: getNumberFromQueryString(searchParams, 'limit', 50),
      offset: getNumberFromQueryString(searchParams, 'offset', 0),
      isUnmatchedWire: true,
      clientNameContains: searchParams.get('clientNameContains') || null,
      email: searchParams.get('email') || null,
      systemMatchingResult:
        (searchParams.get('systemMatchingResult') as ESystemMatchingResult) || ESystemMatchingResult.Sure,
    }
  }, [searchParams])

  const updatePartialFilterValues = useCallback(
    (newValues: Partial<InteracWireFiltersType>) => {
      setSearchParams((prev) => {
        const updatedSearchParams = new URLSearchParams(prev)
        Object.entries(newValues).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            updatedSearchParams.set(key, value.toString())
          } else {
            updatedSearchParams.delete(key)
          }
        })
        return updatedSearchParams
      })
    },
    [setSearchParams],
  )

  const onPaginationModelChange = useCallback(
    (model: GridPaginationModel) => {
      updatePartialFilterValues({
        limit: model.pageSize,
        offset: model.page * model.pageSize,
      })
    },
    [updatePartialFilterValues],
  )

  const onFiltersChange = useCallback(
    (newFilters: Partial<InteracWireFiltersType>) => updatePartialFilterValues(newFilters),
    [updatePartialFilterValues],
  )

  const handleMatchDialogClose = useCallback(() => {
    setSelectedDetailInteracWire(null)
  }, [])
  const handleOnCloseDelete = useCallback(() => {
    setSelectedDeleteInteracWire(null)
  }, [])
  const handleOnCloseUnknown = useCallback(() => {
    setSelectedUnknownInteracWire(null)
  }, [])

  const columns = useUnmatchedInteracWireColumns({
    onView: setSelectedDetailInteracWire,
    onViewUnknown: setSelectedUnknownInteracWire,
    onDelete: setSelectedDeleteInteracWire,
  })
  const [data, isPending] = useInteracWireByFilters(filterValues)

  return (
    <>
      <Paper>
        <UnmatchedInteracWireFilters defaultValues={filterValues} onFiltersChange={onFiltersChange} />
        <DataGrid
          disableColumnFilter
          disableRowSelectionOnClick
          rows={data}
          columns={columns}
          paginationModel={{ pageSize: filterValues.limit, page: filterValues.offset / filterValues.limit }}
          onPaginationModelChange={onPaginationModelChange}
          loading={isPending}
          paginationMode="server"
          rowCount={data.length}
        />
      </Paper>
      <InteracWireMatchDialog interacWire={selectedDetailInteracWire} onClose={handleMatchDialogClose} />
      <InteracWireDeleteDialog interacWire={selectedDeleteInteracWire} onClose={handleOnCloseDelete} />
      <InteracWireMatchUnknownDialog interacWire={selectedUnknownInteracWire} onClose={handleOnCloseUnknown} />
    </>
  )
}

export default UnmatchedInteracWireList
